import { useState, useEffect } from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import Input from "../ui/Input";
import {
  useUpdatePermission,
  useCreatePermission,
  useGetPermissions,
  useTotalStaff,
  useProjects,
  useUpdateProjectName
} from "../Use-auth";

import SucessToast from "./SucessToast";

const permissionsData = [
  { label: "Home", key: "home", children: [{ label: "Home View", key: "home_view" }] },
  {
    label: "Attendance",
    key: "attendance",
    children: [
      { label: "Attendance", key: "attendance_view" },
      {
        label: "Leave", key: "leave_view",
      },

    ],
  },
  {
    label: "Project",
    key: "project",
    children: [
      { label: "View", key: "project_view" },
      {
        label: "Project Show", key: "project_show",
      },
      { label: "Update Status", key: "status_update" },
    ],
  },
  {
    label: "Sales",
    key: "sales",
    children: [
      { label: "Sales View", key: "sales_view" },
    ],
  }
];

const countCheckedChildren = (permission, selected) => {
  if (!permission.children) return 0;

  return permission.children.reduce((sum, child) => {
    if (child.children) {
      return sum + countCheckedChildren(child, selected);
    }
    return sum + (selected[child.key] ? 1 : 0);
  }, 0);
};

function PermissionItem({
  permission,
  selected,
  toggle,
  search,
  expandedAll,
  indeterminate,
  projects,
  selectedProjects,
  setSelectedProjects,
  userId
}) {
  const hasChildren = permission.children?.length > 0;
  const isChecked = !!selected[permission.key];
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(expandedAll);
  }, [expandedAll]);

  const matches =
    permission.label.toLowerCase().includes(search.toLowerCase()) ||
    permission.children?.some(c =>
      c.label.toLowerCase().includes(search.toLowerCase())
    );

  if (!matches) return null;

  const totalChildren = permission.children?.length || 0;
  const checkedChildren = countCheckedChildren(permission, selected);

  return (
    <div className="ml-2 sm:ml-4 mb-2">
      <div
        className="flex justify-between items-center border rounded px-3 py-2 cursor-pointer"
        onClick={() => setOpen(p => !p)}
      >
        <label className="flex gap-2 items-center text-[11px] zs:text-sm">
          <input
            type="checkbox"
            checked={isChecked}
            ref={el => {
              if (el) el.indeterminate = indeterminate?.[permission.key] || false;
            }}
            onChange={e => {
              e.stopPropagation();
              toggle(permission.key, permission.children || []);
            }}
          />
          {permission.label}
        </label>

        {hasChildren && (
          <div className="flex items-center gap-3 text-gray-500">
            <span className="text-[10px] zs:text-[12px]">{checkedChildren}/{totalChildren}</span>
            {open ? <ChevronUp /> : <ChevronDown />}
          </div>
        )}
      </div>

      {hasChildren && open && (
        <div className="mt-1">
          {permission.children.map(child => (
            <div key={child.key}>
              <PermissionItem
                permission={child}
                selected={selected}
                toggle={toggle}
                search={search}
                expandedAll={expandedAll}
                indeterminate={indeterminate}
                projects={projects}
                selectedProjects={selectedProjects}
                setSelectedProjects={setSelectedProjects}
                userId={userId}
              />

              {/* Show projects when Project Show is checked */}
              {child.key === "project_show" && selected[child.key] && (
                <div className="ml-8 mt-2 flex flex-wrap gap-2 mb-[10px]">
                  {projects.map(p => (
                    <label key={p._id} className="flex items-center gap-2 border rounded px-2 py-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedProjects[userId]?.includes(p._id.toString()) || false}
                        onChange={e => {
                          setSelectedProjects(prev => {
                            const current = prev[userId] || [];
                            let nextList = [];
                            if (e.target.checked) {
                              nextList = [...current, p._id.toString()];
                            } else {
                              nextList = current.filter(id => id !== p._id.toString());
                            }
                            return { ...prev, [userId]: nextList };
                          });
                        }}
                      />
                      {p.projectName}
                    </label>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function EmployeePermissions({ adminId }) {
  const [selected, setSelected] = useState({});
  const [search, setSearch] = useState("");
  const [expandedAll, setExpandedAll] = useState(false);
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [toast, setToast] = useState({ show: false, message: "", type: "success" });
  const { data: staffData = [] } = useTotalStaff();
  const { data: permissionsApi = [], refetch } = useGetPermissions();
  const updateProjectNameMutation = useUpdateProjectName();
  const createPermission = useCreatePermission();
  const updatePermission = useUpdatePermission();
  const { data: projects = [] } = useProjects();
  const [selectedProjects, setSelectedProjects] = useState({});
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [saving, setSaving] = useState(false);

  const employeeUsers = staffData
    .map(emp => ({
      userId: emp.employeeId,
      fullName: `${emp.firstName || ""} ${emp.lastName || ""}`.trim(),
      username: emp.officeEmail,
      department: emp.department || emp.departmentName || "",
    }))
    .filter(emp => !["Admin", "Management", "HR"].includes(emp.department));

  useEffect(() => {
    const next = {};
    selectedEmployeeIds.forEach(userId => {
      const existing = permissionsApi.find(p => p.userId === userId);
      const projectIds = Array.isArray(existing?.employees?.project_id)
        ? existing.employees.project_id.map(String)
        : [];
      next[userId] = projectIds;
    });
    setSelectedProjects(next);
  }, [selectedEmployeeIds, permissionsApi]);

  useEffect(() => {
    const next = {};
    selectedEmployeeIds.forEach(userId => {
      const existing = permissionsApi.find(p => p.userId === userId);
      next[userId] = existing?.employees || {};
    });
    setSelected(next);
  }, [selectedEmployeeIds, permissionsApi]);

  const togglePermissionForUser = (userId, key, children = []) => {
    setSelected(prev => {
      const userPerm = { ...(prev[userId] || {}) };
      let value = !userPerm[key];
      userPerm[key] = value;

      if (children.length) {
        const toggleChildren = list => {
          list.forEach(c => {
            userPerm[c.key] = value;
            if (c.children) toggleChildren(c.children);
          });
        };
        toggleChildren(children);
      }

      return { ...prev, [userId]: userPerm };
    });
  };

  const toggleSelectAll = () => {
    const keys = [];
    const collect = list =>
      list.forEach(i => {
        keys.push(i.key);
        if (i.children) collect(i.children);
      });
    collect(permissionsData);

    const next = { ...selected };

    selectedEmployeeIds.forEach(userId => {
      const allChecked = keys.every(k => next[userId]?.[k]);
      const perms = {};
      keys.forEach(k => (perms[k] = !allChecked));
      next[userId] = perms;
    });

    setSelected(next);
  };

  const getMergedSelected = () => {
    const merged = {};
    const indeterminate = {};

    const walk = perm => {
      let all = true;
      let some = false;

      selectedEmployeeIds.forEach(uid => {
        const p = selected[uid] || {};
        if (p[perm.key]) some = true;
        else all = false;
      });

      merged[perm.key] = all;
      indeterminate[perm.key] = some && !all;

      perm.children?.forEach(walk);
    };

    permissionsData.forEach(walk);
    return { merged, indeterminate };
  };

  const { merged, indeterminate } = getMergedSelected();

  const userIdToMongoId = {};

  staffData.forEach(emp => {
    userIdToMongoId[emp.employeeId] = emp._id;
  });

  const arrayEqual = (a = [], b = []) => {
    if (a.length !== b.length) return false;
    const sa = [...a].sort();
    const sb = [...b].sort();
    return sa.every((v, i) => v === sb[i]);
  };

  const handleSave = async () => {
    if (!selectedEmployeeIds.length) return;

    try {
      setSaving(true);

      const projectUpdates = new Map();

      // STEP 1: Prepare project updates
      selectedEmployeeIds.forEach(employeeId => {
        const selectedIds = selectedProjects[employeeId] || [];

        selectedIds.forEach(pid => {
          const project = projects.find(p => p._id?.toString() === pid);
          if (!project) return;

          const current = Array.isArray(project.sharedWith) ? project.sharedWith : [];
          const next = current.includes(employeeId) ? current : [...current, employeeId];

          if (!arrayEqual(current, next)) {
            projectUpdates.set(pid, next);
          }
        });
      });

      // STEP 2: Update projects
      for (const [projectId, sharedWith] of projectUpdates.entries()) {
        const project = projects.find(p => p._id.toString() === projectId);
        if (!project) continue;

        await updateProjectNameMutation.mutateAsync({
          projectId,
          projectName: project.projectName,
          sharedWith,
          isMultiProject: project.isMultiProject,
        });
      }

      // STEP 3: Save permissions
      for (const userId of selectedEmployeeIds) {
        const perms = selected[userId] || {};
        const employeesPerm = {};

        Object.keys(perms).forEach(k => {
          if (perms[k]) employeesPerm[k] = true;
        });

        if (employeesPerm.project_show) {
          employeesPerm.project_id = Array.isArray(selectedProjects[userId])
            ? selectedProjects[userId].map(String)
            : [];
        }

        const existing = permissionsApi.find(p => p.userId === userId);

        const existingProjectIds = Array.isArray(existing?.employees?.project_id)
          ? existing.employees.project_id.map(String)
          : [];

        if (existing) {
          await updatePermission.mutateAsync({
            id: existing._id,
            employees: {
              ...employeesPerm,
              project_id: employeesPerm.project_show
                ? employeesPerm.project_id
                : existingProjectIds,
            },
            management: {},
          });
        } else {
          const userDepartment =
            employeeUsers.find(u => u.userId === userId)?.department || "";

          await createPermission.mutateAsync({
            adminBy: adminId,
            userId,
            department: userDepartment,
            employees: employeesPerm,
            management: {},
          });
        }
      }

      refetch();

      setToast({
        show: true,
        message: "Permissions updated successfully",
        type: "success",
      });

    } catch (err) {
      setToast({
        show: true,
        message: err.message || "Failed to save",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const isPermissionReady = selectedEmployeeIds.every(
    uid => selected[uid] && Object.keys(selected[uid]).length > 0
  );

  return (
    <>
      <div className="mb-6 font-sans">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
          <label className="font-semibold text-[14px] sm:text-[18px] text-gray-800">Select Employee(s)</label>
          <select
            className="border border-gray-300 rounded-lg px-4 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition"
            value={selectedDepartment}
            onChange={(e) => setSelectedDepartment(e.target.value)}
          >
            <option value="">ALL Employee(s)</option>
            {[...new Set(employeeUsers.map(emp => emp.department))].map((department, index) => (
              <option key={index} value={department}>{department}</option>
            ))}
          </select>
        </div>

        {/* Employee List Container */}
        <div className="border border-gray-200 rounded-xl shadow-md p-2 sm:p-5 bg-white">
          {/* Select All */}
          <label className="flex items-center gap-3 mb-4 px-4 py-2 rounded-lg border border-gray-300 cursor-pointer hover:bg-gray-50 transition">
            <input
              type="checkbox"
              checked={selectedEmployeeIds.length === employeeUsers.length}
              onChange={e =>
                setSelectedEmployeeIds(
                  e.target.checked ? employeeUsers.map(u => u.userId) : []
                )
              }
              className="w-4 h-4 text-blue-600 border-gray-300 rounded "
            />
            <span className="font-medium text-gray-700 text-[11px] sm:text-sm text-xs">Select All Users</span>
          </label>

          {/* Employee Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 max-h-72 overflow-y-auto">
            {employeeUsers
              .filter(u => selectedDepartment ? u.department === selectedDepartment : true)
              .map(u => (
                <label
                  key={u.userId}
                  className="flex items-center gap-3 p-3 border border-gray-300 rounded-lg cursor-pointer hover:shadow-lg hover:bg-gray-50 transition"
                >
                  <input
                    type="checkbox"
                    checked={selectedEmployeeIds.includes(u.userId)}
                    onChange={e =>
                      setSelectedEmployeeIds(
                        e.target.checked
                          ? [...selectedEmployeeIds, u.userId]
                          : selectedEmployeeIds.filter(id => id !== u.userId)
                      )
                    }
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded "
                  />
                  <div className="flex flex-col">
                    <span className="font-medium text-[13px] sm:text-[16px] text-gray-800">{u.fullName}</span>
                    <span className="text-[11px] sm:text-sm text-gray-500">{u.username}</span>
                    <span className="text-[9px] sm:text-xs text-gray-400">{u.department}</span>
                  </div>
                </label>
              ))}
          </div>
        </div>
      </div>

      {selectedEmployeeIds.length === 0 ? (
        <div className="p-2 sm:p-4 border rounded-lg bg-gray-100 text-sm text-gray-700">
          Select at least one employee to assign permissions
        </div>
      ) : (
        <div className="p-2 sm:p-4 border rounded-lg bg-gray-50">
          {/* Search Input */}
          <Input
            placeholder="Search permissions..."
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="mb-4 w-full border-gray-300 focus:ring-blue-500 focus:border-blue-500"
          />

          {/* Header: Select All + Actions */}
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-3 my-4">
            {/* Select All Permissions */}
            <label className="flex items-center gap-2 text-gray-700 text-[12px] sm:text-[14px]">
              <input
                type="checkbox"
                checked={Object.values(merged).every(Boolean)}
                ref={el => {
                  if (el) {
                    const some = Object.values(merged).some(v => v);
                    const all = Object.values(merged).every(v => v);
                    el.indeterminate = some && !all;
                  }
                }}
                onChange={toggleSelectAll}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500"
              />
              Select All Permissions
            </label>

            {/* Action Buttons */}
            <div className="flex gap-2 mt-2 md:mt-0">
              <button
                onClick={handleSave}
                disabled={!isPermissionReady || saving}
                className={`px-3 py-1 text-[11px] zs:text-sm rounded-lg border flex items-center gap-2
    ${saving
                    ? "bg-green-200 text-green-700 border-green-600 opacity-70 cursor-not-allowed"
                    : isPermissionReady
                      ? "bg-green-100 text-green-700 border-green-600 hover:bg-green-200"
                      : "bg-gray-100 text-gray-400 border-gray-400 cursor-not-allowed"
                  }`}
              >
                {saving ? (
                  <>
                    Saving...
                    <span className="loader-circle"></span>
                  </>
                ) : (
                  "Save"
                )}
              </button>

              <button
                onClick={() => setExpandedAll(p => !p)}
                className="px-3 py-1 text-[11px] zs:text-sm rounded-lg bg-blue-100 text-blue-700 border border-blue-600 hover:bg-blue-200 transition"
              >
                {expandedAll ? "Collapse All" : "Expand All"}
              </button>
            </div>
          </div>

          {/* Permissions per Employee */}
          <div className="flex flex-col gap-4">
            {selectedEmployeeIds.map(userId => {
              const currentUser = employeeUsers.find(u => u.userId === userId);
              const currentUserDepartment = currentUser?.department;

              return (
                <div key={userId} className="p-2 sm:p-4 border rounded-lg bg-white shadow-sm">
                  <div className="font-semibold text-gray-800 mb-3">
                    {currentUser?.fullName}
                  </div>

                  <div className="flex flex-col gap-2">
                    {permissionsData
                      .filter(perm => {
                        switch (currentUserDepartment) {
                          case "Sales":
                            return perm.key !== "project";
                          default:
                            return perm.key !== "sales";
                        }
                      })
                      .map(perm => (
                        <PermissionItem
                          key={perm.key}
                          permission={perm}
                          selected={selected[userId] || {}}
                          toggle={(key, children) =>
                            togglePermissionForUser(userId, key, children)
                          }
                          search={search}
                          expandedAll={expandedAll}
                          indeterminate={indeterminate}
                          projects={projects}
                          selectedProjects={selectedProjects}
                          setSelectedProjects={setSelectedProjects}
                          userId={userId}
                        />
                      ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {toast.show && (
        <SucessToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </>
  );
}
